import React, { Component } from 'react';
import styled from 'styled-components';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { setSidePanel, closeSidePanel } from 'reducers/sidePanel/actions';
import { toggleAccountMenuDropdown } from 'reducers/customer/actions';

import Tree from 'components/Tree';
import Text from 'components/Text';
import { default as HasRoleSubmenu } from 'components/Submenu';
import MapBreadcrumbs from 'components/MapBreadcrumbs';
import IndeNodeMap from 'components/IndeNodeMap';
import AggregatorMap from 'components/AggregatorMap';

import { ReactComponent as ZoomInIcon } from 'assets/zoom-in-icon.svg';
import { ReactComponent as ZoomOutIcon } from 'assets/zoom-out-icon.svg';
import { ReactComponent as PrimacyKeySvg } from 'assets/primacy-key.svg';
import { ReactComponent as RemoteLocalKeySvg } from 'assets/local-remote-key.svg';
import { ReactComponent as NodeDeviceKeySvg } from 'assets/node-device-key.svg';
import { ReactComponent as LoaderIcon } from 'assets/loader-icon-with-text.svg';

import {
  RESOURCE_TYPES,
  SUBMENU_TYPES,
  WRITE_ACCESS_AUTH_ROLES,
  SIDEPANEL_STATES
} from 'utils/appConstants';
import {
  subscribe,
  unsubscribe,
  POLL_TOPONODES,
  POLL_DEVICES,
  POLL_INDENODES,
  POLL_AGGREGATORS
} from 'reducers/polling/actions';

const externallyManagedEdevs =
  window.env.REACT_APP_EXTERNALLY_MANAGED_EDEVS === 'true';

const Submenu = HasRoleSubmenu(WRITE_ACCESS_AUTH_ROLES);

const pollRate = parseInt(window.env.REACT_APP_GLOBAL_POLL_RATE_FREQUENCY);

const TopologyPage = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  flex: 1;
`;

const TreeContainer = styled.div`
  background-color: #ffffff;
  border-radius: 4px 4px 0 0;
  box-shadow: 0 0 6px 0 rgba(0, 0, 0, 0.2);
  display: flex;
  flex-flow: column;
  flex: 1;
  justify-content: center;
  margin-left: 12px;
  margin-right: 12px;
  z-index: 1;
  overflow-y: auto;
`;

const TopologyTreeContainer = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
`;

const TabContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  box-sizing: border-box;
  margin-left: 12px;
  min-height: 72px;
  padding-left: 0.75em;
  z-index: 1;
`;

const Tab = styled.button`
  cursor: pointer;
  background-color: rgba(249, 251, 252, 0.75);
  border: none;
  border-radius: 0 0 4px 4px;
  box-shadow: 0 0 6px 0 rgba(0, 0, 0, 0.12);
  height: 27px;
  margin: 0 0.35em;
  outline: none;
  padding: 5px 16px;

  &[aria-current] {
    background-color: #0779af;
  }
`;

const TabText = styled(Text)`
  color: ${({ isCurrent }) => (isCurrent ? '#F9FBFC' : '#0D151F')};
  font-size: 14px;
  font-weight: 500;
  margin: 0;
`;

const ZoomIconsContainer = styled.div`
  align-self: flex-start;
  padding: 0.5em 0;
  margin-left: auto;

  svg {
    margin: 0 0.4em;
  }
`;

const ZoomButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  outline: none;
  padding: 0;

  &:hover {
    opacity: 0.75;
  }
`;

const LoaderIconContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const KeyContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  right: 1em;
  bottom: 1em;
`;

class Topology extends Component {
  constructor(props) {
    super(props);

    this.treeComponent = null;

    this.state = {
      currentTab: RESOURCE_TYPES.TOPONODE,
      showSubmenuDropdown: false,
      isFetchingData: false
    };
  }

  async componentDidMount() {
    const { subscribe } = this.props;
    const { currentTab } = this.state;

    const pollTypes = [POLL_DEVICES];
    if (currentTab === RESOURCE_TYPES.TOPONODE) {
      pollTypes.push(POLL_TOPONODES);
    } else if (currentTab === RESOURCE_TYPES.INDENODE) {
      pollTypes.push(POLL_INDENODES);
    } else if (currentTab === RESOURCE_TYPES.AGGREGATOR) {
      pollTypes.push(POLL_AGGREGATORS);
    }
    subscribe({
      subscriber: this.constructor.name,
      pollTypes: pollTypes.map(pollType => ({ type: pollType })),
      pollRate
    });

    this.setState({ isFetchingData: false });
  }

  componentDidUpdate(prevProps, prevState) {
    const { subscribe, unsubscribe } = this.props;
    const { currentTab } = this.state;

    if (prevState.currentTab !== currentTab) {
      unsubscribe(this.constructor.name);
      const pollTypes = [POLL_DEVICES];
      if (currentTab === RESOURCE_TYPES.TOPONODE) {
        pollTypes.push(POLL_TOPONODES);
      } else if (currentTab === RESOURCE_TYPES.INDENODE) {
        pollTypes.push(POLL_INDENODES);
      } else if (currentTab === RESOURCE_TYPES.AGGREGATOR) {
        pollTypes.push(POLL_AGGREGATORS);
      }
      subscribe({
        subscriber: this.constructor.name,
        pollTypes: pollTypes.map(pollType => ({ type: pollType })),
        pollRate
      });
    }
  }

  componentWillUnmount() {
    const { unsubscribe } = this.props;
    unsubscribe(this.constructor.name);
  }

  toggleMapTab = async tab => {
    this.setState({ currentTab: tab });
  };

  openCreatePanel = async (e, panelType) => {
    const { setSidePanel, closeSidePanel } = this.props;
    e.preventDefault();
    await closeSidePanel();
    setSidePanel({ entity: panelType, state: SIDEPANEL_STATES.CREATE });
    this.setState({ showSubmenuDropdown: false });
  };

  toggleMenu = () => {
    if (this.props.showAcctMenuDropdown) {
      this.props.toggleAccountMenuDropdown(false);
    }
    if (this.state.showSubmenuDropdown) {
      this.setState({ showSubmenuDropdown: false });
    }
  };

  render() {
    const { currentTab, showSubmenuDropdown, isFetchingData } = this.state;

    const isShowingTopoNodes = currentTab === RESOURCE_TYPES.TOPONODE;
    const isShowingIndeNodes = currentTab === RESOURCE_TYPES.INDENODE;
    const isShowingAggregators = currentTab === RESOURCE_TYPES.AGGREGATOR;

    return (
      <TopologyPage onClick={this.toggleMenu}>
        <Submenu
          dropdownIsShowing={showSubmenuDropdown}
          selectOption={this.openCreatePanel}
          toggleDropdownVisibility={isShowing =>
            this.setState({ showSubmenuDropdown: isShowing })
          }
          submenuType={SUBMENU_TYPES.TOPOLOGY}
        />
        <MapBreadcrumbs currentTab={currentTab} />

        <TreeContainer>
          {isFetchingData ? (
            <LoaderIconContainer>
              <LoaderIcon />
            </LoaderIconContainer>
          ) : isShowingTopoNodes ? (
            // TODO: refactor to a TopoNode topology map component
            <TopologyTreeContainer>
              <Tree
                onRef={ref => (this.treeComponent = ref)}
                resourceTab={RESOURCE_TYPES.TOPONODE}
              />
              <KeyContainer>
                {externallyManagedEdevs ? (
                  <RemoteLocalKeySvg />
                ) : (
                  <NodeDeviceKeySvg />
                )}
                <PrimacyKeySvg />
              </KeyContainer>
            </TopologyTreeContainer>
          ) : isShowingIndeNodes ? (
            <IndeNodeMap onRef={ref => (this.treeComponent = ref)} />
          ) : isShowingAggregators ? (
            <AggregatorMap onRef={ref => (this.treeComponent = ref)} />
          ) : null}
        </TreeContainer>

        <TabContainer>
          <Tab
            aria-current={isShowingTopoNodes ? true : null}
            onClick={() => {
              this.toggleMapTab(RESOURCE_TYPES.TOPONODE);
              window.scrollTo(0, 0);
            }}
          >
            <TabText isCurrent={isShowingTopoNodes}>Topology Map</TabText>
          </Tab>
          <Tab
            aria-current={isShowingIndeNodes ? true : null}
            onClick={() => {
              this.toggleMapTab(RESOURCE_TYPES.INDENODE);
              window.scrollTo(0, 0);
            }}
          >
            <TabText isCurrent={isShowingIndeNodes}>Independent Nodes</TabText>
          </Tab>
          <Tab
            aria-current={isShowingAggregators ? true : null}
            onClick={() => {
              this.toggleMapTab(RESOURCE_TYPES.AGGREGATOR);
              window.scrollTo(0, 0);
            }}
          >
            <TabText isCurrent={isShowingAggregators}>Aggregators</TabText>
          </Tab>

          <ZoomIconsContainer>
            <ZoomButton
              onClick={() => {
                this.treeComponent.triggerIncrementalZoomIn();
              }}
            >
              <ZoomInIcon />
            </ZoomButton>
            <ZoomButton
              onClick={() => {
                this.treeComponent.triggerIncrementalZoomOut();
              }}
            >
              <ZoomOutIcon />
            </ZoomButton>
          </ZoomIconsContainer>
        </TabContainer>
      </TopologyPage>
    );
  }
}

const mapStateToProps = (state, props) => {
  return {
    topoNodes: state.topoNode.topoNodes,
    indeNodes: state.indeNode.indeNodes,
    aggregators: state.aggregator.aggregators,
    curves: state.curve.curves,
    controls: state.control.controls,
    showAcctMenuDropdown: state.customer.showAcctMenuDropdown
  };
};

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      setSidePanel,
      closeSidePanel,
      subscribe,
      unsubscribe,
      toggleAccountMenuDropdown
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(Topology);
